/**
 * Endpoints de **Gestión → Administración → Cotización**
 * (`/api/gestion/cotizaciones/*`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CatalogosCotizacionRespuesta,
  CotizacionDetalleRespuesta,
  CotizacionesRespuesta,
  NuevaCotizacion,
} from './cotizacion.types';

const CLAVE = 'gestion';

export const cotizacionKeys = {
  listado: () => [CLAVE, 'cotizaciones', 'listado'] as const,
  catalogos: () => [CLAVE, 'cotizaciones', 'catalogos'] as const,
  detalle: (id: number | null) => [CLAVE, 'cotizaciones', 'detalle', id] as const,
};

export const cotizacionApi = {
  listado: (signal?: AbortSignal): Promise<CotizacionesRespuesta> =>
    apiClient.get<CotizacionesRespuesta>('/api/gestion/cotizaciones', { signal }),

  catalogos: (signal?: AbortSignal): Promise<CatalogosCotizacionRespuesta> =>
    apiClient.get<CatalogosCotizacionRespuesta>('/api/gestion/cotizaciones/catalogos', { signal }),

  detalle: (id: number, signal?: AbortSignal): Promise<CotizacionDetalleRespuesta> =>
    apiClient.get<CotizacionDetalleRespuesta>(`/api/gestion/cotizaciones/${id}`, { signal }),

  crear: (datos: NuevaCotizacion): Promise<{ cotizacion_id: number; serie: string; total: number }> =>
    apiClient.post('/api/gestion/cotizaciones', { body: datos }),
};

/** Listado de cotizaciones. */
export function useCotizaciones() {
  return useQuery({
    queryKey: cotizacionKeys.listado(),
    queryFn: ({ signal }) => cotizacionApi.listado(signal),
  });
}

/** Catálogos del alta. */
export function useCatalogosCotizacion() {
  return useQuery({
    queryKey: cotizacionKeys.catalogos(),
    queryFn: ({ signal }) => cotizacionApi.catalogos(signal),
  });
}

/** Detalle (solo se consulta cuando hay id). */
export function useCotizacionDetalle(id: number | null) {
  return useQuery({
    queryKey: cotizacionKeys.detalle(id),
    queryFn: ({ signal }) => cotizacionApi.detalle(id as number, signal),
    enabled: id !== null,
  });
}

/** Alta de cotización con refresco del listado. */
export function useCrearCotizacion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (datos: NuevaCotizacion) => cotizacionApi.crear(datos),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cotizacionKeys.listado() });
    },
  });
}
